/**
 * Debug Panel Component for AI Summary Tool
 * Displays AI prompts, parameters, and cost information
 */

import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Grid,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';

const DebugPanel = ({ debug, data, mode }) => {
  if (!debug && !data) {
    return null;
  }

  // Calculate estimated cost (OpenAI pricing as of 2024)
  const costPer1kTokens = 0.0015; // GPT-3.5-turbo approximate cost
  const estimatedCost = ((debug?.tokens_used || 0) / 1000 * costPer1kTokens).toFixed(4);

  const isOpenAI = debug?.model && debug.model !== 'mock';
  const isStored = mode === 'stored';

  // Determine display mode
  const getDisplayMode = () => {
    if (isOpenAI) return { label: 'OpenAI API Data', color: 'success.main', icon: '✅' };
    if (isStored) return { label: 'Stored Summary Data', color: 'info.main', icon: '📁' };
    return { label: 'Mock Mode Data', color: 'warning.main', icon: '⚠️' };
  };

  const displayMode = getDisplayMode();

  return (
    <Accordion sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <SettingsIcon color="primary" fontSize="small" />
          <Typography variant="body2" fontWeight="medium">
            Debug: {displayMode.label}
          </Typography>
          {isOpenAI && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {debug?.tokens_used || 0} tokens ≈ ${estimatedCost}
            </Typography>
          )}
          {!isOpenAI && !isStored && (
            <Typography variant="caption" sx={{ ml: 'auto', color: 'warning.main', fontWeight: 'medium' }}>
              ⚠️ MOCK MODE
            </Typography>
          )}
          {isStored && (
            <Typography variant="caption" sx={{ ml: 'auto', color: 'info.main', fontWeight: 'medium' }}>
              📁 STORED
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Alert severity="info" sx={{ mb: 2 }}>
          This shows exactly what data and prompts were sent to the AI model
        </Alert>

        <Grid container spacing={2}>
          {/* AI Parameters */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
              <Typography variant="body2">
                <strong>Model:</strong> {debug.model || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Verbosity:</strong> {debug.verbosity || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Reasoning Effort:</strong> {debug.reasoning_effort || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Tokens Used:</strong> {debug.tokens_used || 0}
              </Typography>
            </Box>
          </Grid>

          {/* System Prompt */}
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>System Prompt (Role Instructions):</strong>
            </Typography>
            <Box sx={{
              bgcolor: 'grey.900',
              color: 'grey.100',
              p: 2,
              borderRadius: 1,
              fontSize: '12px',
              fontFamily: 'monospace',
              maxHeight: 200,
              overflowY: 'auto',
              whiteSpace: 'pre-wrap'
            }}>
              {debug.system_prompt || 'No system prompt available'}
            </Box>
          </Grid>

          {/* User Prompt */}
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>User Prompt (Actual Request with Data):</strong>
            </Typography>
            <Box sx={{
              bgcolor: 'grey.900',
              color: 'grey.100',
              p: 2,
              borderRadius: 1,
              fontSize: '12px',
              fontFamily: 'monospace',
              maxHeight: 300,
              overflowY: 'auto',
              whiteSpace: 'pre-wrap'
            }}>
              {debug.user_prompt || 'No user prompt available'}
            </Box>
          </Grid>

          {/* Full Data Object */}
          {data && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Full Backend Data:</strong>
              </Typography>
              <Box sx={{
                bgcolor: 'grey.900',
                color: 'grey.100',
                p: 2,
                borderRadius: 1,
                fontSize: '11px',
                fontFamily: 'monospace',
                maxHeight: 400,
                overflowY: 'auto'
              }}>
                <pre>{JSON.stringify(data, null, 2)}</pre>
              </Box>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default DebugPanel;

